import { useState } from 'react';

import { Markdown } from '@/components/ui/smoothdown';
import { cn } from '@/lib/utils';

import {
  fieldErrorClass,
  FIELD_ERROR_PLACEHOLDER_CLASS,
  textAppearanceClass,
} from '../../field/textAppearance';
import { fieldErrorId } from '../../fieldIds';
import { MrkdwnText } from '../../internals/MrkdwnText';
import { useCardMetrics } from '../../internals/useCardMetrics';
import {
  LINE_CLAMP,
  MARKDOWN_CLASS,
  TEXT_BOX_CLASS,
  TEXT_STYLE,
} from '../../tokens';

import { BODY_CONTENT_CLASS, RichEditor } from './RichEditor';

import type { EditorProps } from './PlainEditor';

export type MarkdownEditorProps = EditorProps & {
  format: 'markdown' | 'mrkdwn';
};

export const MarkdownEditor = ({ format, ...props }: MarkdownEditorProps) => {
  const [editing, setEditing] = useState(props.autoFocus ?? false);
  const metrics = useCardMetrics();
  if (editing) {
    return (
      <RichEditor
        {...props}
        format={format}
        onBlur={() => {
          props.onBlur();
          setEditing(false);
        }}
      />
    );
  }
  return (
    <button
      id={props.id}
      type="button"
      aria-invalid={props.error ? true : undefined}
      aria-describedby={props.error ? fieldErrorId(props.id) : undefined}
      className={cn(
        TEXT_BOX_CLASS,
        textAppearanceClass(props.appearance, props.outline),
        TEXT_STYLE[props.textStyle],
        props.bodyClass,
        props.toneClass,
        metrics.bodyMaxHeight,
        'min-h-[1lh] overflow-y-auto py-0 text-left cursor-text',
        props.error && fieldErrorClass(props.appearance, props.outline)
      )}
      onClick={() => setEditing(true)}
      onFocus={() => {
        setEditing(true);
        props.onFocus();
      }}
    >
      <span
        className={
          props.lines
            ? LINE_CLAMP[Math.min(props.lines, 10) - 1]
            : BODY_CONTENT_CLASS
        }
      >
        {!props.value.trim() ? (
          <span
            className={
              props.error
                ? FIELD_ERROR_PLACEHOLDER_CLASS.text
                : 'text-foreground-muted'
            }
          >
            {props.placeholder}
          </span>
        ) : format === 'mrkdwn' ? (
          <MrkdwnText text={props.value} />
        ) : (
          <Markdown
            id={props.id}
            content={props.value}
            className={cn(
              MARKDOWN_CLASS,
              TEXT_STYLE[props.textStyle],
              props.bodyClass,
              props.toneClass
            )}
          />
        )}
      </span>
    </button>
  );
};
